import { useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { useNotificationStore } from '@store'
import NotificationItem from './NotificationItem'
import './NotificationDropdown.scss'

/**
 * NotificationDropdown Component
 *
 * Dropdown panel displaying list of notifications.
 * Supports mark as read, mark all as read, and navigation to linked items.
 */
const NotificationDropdown = ({
	isOpen,
	onClose,
	maxItems = 10
}) => {
	const navigate = useNavigate()
	const {
		notifications,
		unreadCount,
		loading,
		fetchNotifications,
		markAsRead,
		markAllAsRead
	} = useNotificationStore()

	// Fetch notifications when dropdown opens
	useEffect(() => {
		if (isOpen) {
			fetchNotifications()
		}
	}, [isOpen, fetchNotifications])

	// Handle clicking a notification
	const handleNotificationClick = useCallback((notification) => {
		if (notification.link) {
			navigate(notification.link)
			onClose()
		}
	}, [navigate, onClose])

	// Handle mark all as read
	const handleMarkAllRead = useCallback(() => {
		markAllAsRead()
	}, [markAllAsRead])

	// Handle keyboard navigation
	const handleKeyDown = useCallback((e) => {
		if (e.key === 'Escape') {
			onClose()
		}
	}, [onClose])

	// Get displayed notifications (limited)
	const displayedNotifications = notifications.slice(0, maxItems)
	const hasMore = notifications.length > maxItems

	if (!isOpen) return null

	return (
		<div
			className="notification-dropdown"
			role="dialog"
			aria-label="Notifications"
			onKeyDown={handleKeyDown}
		>
			{/* Header */}
			<div className="notification-dropdown__header">
				<h3 className="notification-dropdown__title">
					Notifications
					{unreadCount > 0 && (
						<span className="notification-dropdown__count">
							{unreadCount}
						</span>
					)}
				</h3>

				{unreadCount > 0 && (
					<button
						className="notification-dropdown__action"
						onClick={handleMarkAllRead}
						type="button"
					>
						Mark all as read
					</button>
				)}
			</div>

			{/* Content */}
			<div className="notification-dropdown__content">
				{loading && notifications.length === 0 ? (
					<div className="notification-dropdown__loading">
						<div className="notification-dropdown__spinner"></div>
						<span>Loading notifications...</span>
					</div>
				) : displayedNotifications.length === 0 ? (
					<div className="notification-dropdown__empty">
						<span className="notification-dropdown__empty-icon">🔔</span>
						<p>No notifications yet</p>
						<span className="notification-dropdown__empty-hint">
							You&apos;ll see updates about tasks, approvals, and more here
						</span>
					</div>
				) : (
					<div className="notification-dropdown__list">
						{displayedNotifications.map((notification) => (
							<NotificationItem
								key={notification.id}
								notification={notification}
								onClick={handleNotificationClick}
								onMarkAsRead={markAsRead}
							/>
						))}
					</div>
				)}
			</div>

			{/* Footer */}
			{(hasMore || notifications.length > 0) && (
				<div className="notification-dropdown__footer">
					<button
						className="notification-dropdown__view-all"
						onClick={() => {
							navigate('/notifications')
							onClose()
						}}
						type="button"
					>
						View all notifications
					</button>
				</div>
			)}
		</div>
	)
}

NotificationDropdown.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	maxItems: PropTypes.number
}

export default NotificationDropdown
